/**
 * Chart export helpers.
 * - downloadChartSvg: captures the full container as PNG with chart-only fallback.
 * - downloadJson: downloads a JS object as pretty JSON.
 */

import html2canvas from "html2canvas";

export function downloadJson(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function triggerDownload(href, filename) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function downloadCanvasAsPng(canvas, filename, onFailure) {
  const fail = () => {
    if (typeof onFailure === "function") onFailure();
  };

  try {
    canvas.toBlob((blob) => {
      if (blob) {
        const pngUrl = URL.createObjectURL(blob);
        triggerDownload(pngUrl, filename);
        URL.revokeObjectURL(pngUrl);
        return;
      }

      try {
        triggerDownload(canvas.toDataURL("image/png"), filename);
      } catch {
        fail();
      }
    }, "image/png");
  } catch {
    fail();
  }
}

function drawSvgToPng(svgMarkup, width, height, filename, onFailure) {
  const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);
  const fail = () => {
    if (typeof onFailure === "function") onFailure();
  };

  const img = new Image();
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas");
      const scale = Math.max(2, Math.min(4, (window.devicePixelRatio || 1) * 2));
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.fillStyle = "#0A0A0A";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, width, height);

      downloadCanvasAsPng(canvas, filename, fail);
    } catch {
      fail();
    } finally {
      URL.revokeObjectURL(svgUrl);
    }
  };

  img.onerror = () => {
    URL.revokeObjectURL(svgUrl);
    fail();
  };

  img.src = svgUrl;
}

function downloadChartOnlySvg(containerEl, filename) {
  const svg =
    containerEl.querySelector("svg.recharts-surface") ||
    containerEl.querySelector("svg:not(.lucide)");
  if (!svg) return;

  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const rect = svg.getBoundingClientRect();
  const viewBox = svg.viewBox?.baseVal;
  const w =
    Math.round(rect.width || viewBox?.width || Number(svg.getAttribute("width"))) ||
    1200;
  const h =
    Math.round(rect.height || viewBox?.height || Number(svg.getAttribute("height"))) ||
    600;
  clone.setAttribute("width", w);
  clone.setAttribute("height", h);

  const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  bg.setAttribute("width", "100%");
  bg.setAttribute("height", "100%");
  bg.setAttribute("fill", "#0A0A0A");
  clone.insertBefore(bg, clone.firstChild);

  const chartXml = new XMLSerializer().serializeToString(clone);
  drawSvgToPng(chartXml, w, h, filename);
}

function getExportScale(containerEl) {
  const rect = containerEl.getBoundingClientRect();
  const basePixels = Math.max(1, rect.width * rect.height);
  const dpr = window.devicePixelRatio || 1;

  // Keep PNG sharp, but cap total pixels so the page does not freeze during capture.
  const preferredScale = Math.min(2, Math.max(1.2, dpr));
  const maxPixels = 4_000_000;
  const maxScaleByPixels = Math.sqrt(maxPixels / basePixels);
  return Math.max(1, Math.min(preferredScale, maxScaleByPixels));
}

async function downloadCardAsPng(containerEl, filename, onFailure) {
  const markerAttr = "data-export-root";
  const previousMarker = containerEl.getAttribute(markerAttr);
  containerEl.setAttribute(markerAttr, "true");

  try {
    const scale = getExportScale(containerEl);

    // Let the click interaction finish painting before starting heavy capture work.
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const canvas = await html2canvas(containerEl, {
      backgroundColor: "#0A0A0A",
      scale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      imageTimeout: 3000,
      onclone: (doc) => {
        const root = doc.querySelector(`[${markerAttr}="true"]`);
        if (!root) return;

        // Remove transitions/animations in the clone for faster and more stable capture.
        const style = doc.createElement("style");
        style.textContent = `
          [${markerAttr}="true"],
          [${markerAttr}="true"] * {
            animation: none !important;
            transition: none !important;
            caret-color: transparent !important;
          }
        `;
        doc.head.appendChild(style);
      },
    });

    downloadCanvasAsPng(canvas, filename, onFailure);
  } catch {
    if (typeof onFailure === "function") onFailure();
  } finally {
    if (previousMarker === null) {
      containerEl.removeAttribute(markerAttr);
    } else {
      containerEl.setAttribute(markerAttr, previousMarker);
    }
  }
}

export function downloadChartSvg(containerEl, filename) {
  if (!containerEl) return;

  downloadCardAsPng(containerEl, filename, () => {
    downloadChartOnlySvg(containerEl, filename);
  });
}
